"use client";

import { useCallback, useSyncExternalStore } from "react";
import { GitCompareArrows, Check } from "lucide-react";
import { useUser } from "@/lib/user-context";
import { LoginPromptModal, useLoginPrompt } from "@/components/common/login-prompt-modal";

const STORAGE_KEY = "skinagent_compare_items";
const MAX_ITEMS = 5;
const EVENT = "compare-updated";

function subscribe(onChange: () => void) {
  window.addEventListener(EVENT, onChange);
  window.addEventListener("storage", onChange);
  return () => {
    window.removeEventListener(EVENT, onChange);
    window.removeEventListener("storage", onChange);
  };
}

function readItems(): string[] {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
  } catch {
    return [];
  }
}

export function CompareButton({ title }: { title: string }) {
  const { me } = useUser();
  const { modalState, requireLogin, closeModal } = useLoginPrompt();

  const added = useSyncExternalStore(
    subscribe,
    () => readItems().includes(title),
    () => false,
  );

  const doToggle = useCallback(() => {
    const items = readItems();
    if (items.includes(title)) {
      const next = items.filter((t) => t !== title);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } else {
      if (items.length >= MAX_ITEMS) return;
      const next = [...items, title];
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    }
    // 비교 탭 배지 업데이트 + useSyncExternalStore 구독자들 동기화
    window.dispatchEvent(new Event(EVENT));
  }, [title]);

  const handleClick = useCallback(() => {
    requireLogin(!!me?.user, "compare", doToggle);
  }, [me, requireLogin, doToggle]);

  return (
    <>
      <button
        type="button"
        onClick={handleClick}
        className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-2 text-sm font-medium transition-all ${
          added
            ? "border-violet-300 bg-violet-50 text-violet-700"
            : "border-gray-200 bg-white text-gray-600 hover:border-violet-200 hover:bg-violet-50"
        }`}
      >
        {added ? (
          <>
            <Check className="h-4 w-4" />
            비교 담김
          </>
        ) : (
          <>
            <GitCompareArrows className="h-4 w-4" />
            비교 담기
          </>
        )}
      </button>
      <LoginPromptModal open={modalState.open} onClose={closeModal} trigger={modalState.trigger} />
    </>
  );
}
